<template>
    <RedesignBaseCard
        border-color="var(--field-borders)"
        disable-animation
        :class="widthClass"
    >
        <template #content>
            <div
                class="redesign-metric-card"
                :class="{ 'redesign-metric-card--centered': props.width === 'auto' }"
            >
                <slot>
                    <span class="redesign-metric-card__label">{{ label }}</span>
                    <span
                        class="redesign-metric-card__value"
                        :class="valueVariantClass"
                    >
                        {{ value }}
                    </span>
                </slot>
            </div>
        </template>
    </RedesignBaseCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { TranslateResult } from 'vue-i18n'
import RedesignBaseCard from '@/components/RedesignBaseComponents/RedesignBaseCard.vue'

type MetricCardWidth = 'stretch' | 'auto'
type MetricCardValueVariant = 'default' | 'success' | 'danger'

const VALUE_VARIANT_CLASSES: Partial<Record<MetricCardValueVariant, string>> = {
    success: 'redesign-metric-card__value--success',
    danger: 'redesign-metric-card__value--danger'
}

interface Props {
    label?: string | TranslateResult
    value?: string | number
    valueVariant?: MetricCardValueVariant
    width?: MetricCardWidth
}

const props = withDefaults(defineProps<Props>(), {
    label: '',
    value: '',
    valueVariant: 'default',
    width: 'stretch'
})

const widthClass = computed(() =>
    props.width === 'auto' ? 'redesign-metric-card--auto' : 'redesign-metric-card--stretch'
)

const valueVariantClass = computed(() => VALUE_VARIANT_CLASSES[props.valueVariant] ?? '')
</script>

<style scoped lang="scss">
.redesign-metric-card--stretch,
.redesign-metric-card--auto {
    background-color: var(--mainBgColor-color);
    display: flex;
    flex-direction: column;
    align-self: stretch;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.07);
}

.redesign-metric-card--stretch {
    flex: 1 1 0;
    min-width: 0;
}

.redesign-metric-card--auto {
    flex: 0 0 auto;
    transition: width 0.3s ease;
}

.redesign-metric-card {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 8px 10px;
    color: var(--gray-700);

    &--centered {
        align-items: center;
    }

    &__label {
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__value {
        font-size: 18px;
        font-weight: 600;
        line-height: 1;

        &--success {
            color: var(--inCall-color);
        }

        &--danger {
            color: var(--red);
        }
    }
}
</style>
